import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const ShimmerWrapper = ({ children, className = '' }) => (
  <div className={cn('relative overflow-hidden', className)}>
    <div
      className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-background/40 to-transparent animate-shimmer"
      style={{ animation: 'shimmer 2s infinite' }}
    />
    {children}
  </div>
);

export const ShimmerBox = ({ width = 'w-full', height = 'h-4', className = '' }) => (
  <ShimmerWrapper className={cn(width, height, 'rounded-md bg-muted', className)}>
    <div className={cn(width, height, 'rounded-md bg-muted')} />
  </ShimmerWrapper>
);

export const ShimmerText = ({ lines = 3, className = '' }) => (
  <div className={cn('space-y-3', className)}>
    {Array.from({ length: lines }).map((_, index) => (
      <ShimmerBox key={index} width={index === lines - 1 ? 'w-3/4' : 'w-full'} height="h-4" />
    ))}
  </div>
);

export const ShimmerCard = ({ className = '' }) => (
  <Card className={className}>
    <CardContent className="p-6">
      <ShimmerBox width="w-1/3" height="h-6" className="mb-4" />
      <ShimmerText lines={3} />
    </CardContent>
  </Card>
);

export const ShimmerTable = ({ rows = 5, columns = 4, className = '' }) => (
  <Card className={cn('overflow-hidden', className)}>
    <div className="border-b px-6 py-4">
      <div className="flex gap-4">
        {Array.from({ length: columns }).map((_, index) => (
          <ShimmerBox key={index} width="flex-1" height="h-4" />
        ))}
      </div>
    </div>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="border-b px-6 py-4 last:border-b-0">
        <div className="flex gap-4">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <ShimmerBox key={colIndex} width="flex-1" height="h-4" />
          ))}
        </div>
      </div>
    ))}
  </Card>
);

export const ShimmerStats = ({ count = 4, className = '' }) => (
  <div className={cn('grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4', className)}>
    {Array.from({ length: count }).map((_, index) => (
      <Card key={index}>
        <CardContent className="flex items-center justify-between p-6">
          <div className="flex-1">
            <ShimmerBox width="w-1/2" height="h-4" className="mb-2" />
            <ShimmerBox width="w-3/4" height="h-8" />
          </div>
          <ShimmerBox width="w-12" height="h-12" className="rounded-full" />
        </CardContent>
      </Card>
    ))}
  </div>
);

export const ShimmerAvatar = ({ size = 'w-10 h-10', className = '' }) => (
  <ShimmerWrapper className={cn(size, 'rounded-full bg-muted', className)}>
    <div className={cn(size, 'rounded-full bg-muted')} />
  </ShimmerWrapper>
);

export const ShimmerButton = ({ width = 'w-24', height = 'h-10', className = '' }) => (
  <ShimmerWrapper className={cn(width, height, 'rounded-md bg-muted', className)}>
    <div className={cn(width, height, 'rounded-md bg-muted')} />
  </ShimmerWrapper>
);

export const ShimmerPageLayout = ({
  title = true,
  stats = true,
  table = true,
  cards = false,
  className = '',
}) => (
  <div className={cn('p-6', className)}>
    {title ? (
      <div className="mb-8">
        <ShimmerBox width="w-1/4" height="h-8" className="mb-2" />
        <ShimmerBox width="w-1/2" height="h-4" />
      </div>
    ) : null}

    {stats ? <ShimmerStats className="mb-8" /> : null}

    {cards ? (
      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <ShimmerCard key={index} />
        ))}
      </div>
    ) : null}

    {table ? (
      <div className="mb-4">
        <div className="mb-4 flex items-center justify-between">
          <ShimmerBox width="w-64" height="h-10" />
          <ShimmerButton width="w-32" />
        </div>
        <ShimmerTable />
      </div>
    ) : null}
  </div>
);

export default ShimmerWrapper;
